import formatDuration from '../../../helpers/date-time/format-duration'
import formatIntegerBySpace from '../../../helpers/formatters/format-integer-by-space'
import outputCalculatedValue from '../../../helpers/formatters/output-calculated-value'
import { getShipmentMaterialConsumptions } from '../../../helpers/entities/shipment/get-shipment-material-consumptions'
import { formatLayerLabel } from '../../completed-print-jobs/helpers/consumption-helpers'
import { MaterialRequirement } from '../../../types/api/components/material/material-requirements'
import MaterialConsumption from '../../../types/api/material-consumption'
import Shipment from '../../../types/api/shipment'

export type PlanFactDeltaTone = 'cost' | 'profit'

export type PlanFactValueKind = 'number' | 'duration' | 'text'

export type PlanFactColumnGroup = 'general' | 'money' | 'consumables' | 'time'

export interface PlanFactComparisonItem {
    key: string
    label: string
    group: PlanFactColumnGroup
    planned: number | string | null | undefined
    actual: number | string | null | undefined
    delta?: number
    kind: PlanFactValueKind
    deltaTone?: PlanFactDeltaTone
    formatNumber?: (value: number) => string
}

function sumCopies(
    positions: { copies?: number | null; actualCopies?: number | null }[] | undefined,
    mode: 'planned' | 'actual'
): number {
    return (positions ?? []).reduce((sum, position) => {
        if (mode === 'planned') {
            return sum + (Number(position.copies) || 0)
        }
        return sum + (Number(position.actualCopies ?? position.copies) || 0)
    }, 0)
}

function getPlannedFloods(shipment: Shipment): number | undefined {
    if (shipment.stampFloods != null) return shipment.stampFloods
    if (shipment.withoutCutting) return shipment.virtualStamp?.floods
    return shipment.stamp?.floods
}

function formatPlannedMaterials(requirements: MaterialRequirement[] | undefined): string {
    if (!requirements?.length) return '—'

    return requirements
        .map(item => {
            const name = item.material_type?.name || '—'
            const configs = (item.configurations || [])
                .map(config => `${config.material?.width ?? '—'} ${config.amount ?? '—'}км`)
                .join(', ')
            return configs ? `${name}: ${configs}` : name
        })
        .join('; ')
}

function formatActualMaterials(consumptions: MaterialConsumption[]): string {
    if (!consumptions.length) return '—'

    return consumptions
        .map(consumption => {
            const name = consumption.material?.material_type?.name || '—'
            return `${name}: ${consumption.material?.width ?? '—'} ${consumption.amount ?? '—'}км - ${formatLayerLabel(consumption.layerNumber)}`
        })
        .join('; ')
}

const roundTenth = (value: number) => Math.round(value * 10) / 10
const roundHundredth = (value: number) => Math.round(value * 100) / 100
const roundLength = (value: number) => Math.round(value * 1000) / 1000

function numericItem(params: {
    key: string
    label: string
    group: PlanFactColumnGroup
    planned: number | null | undefined
    actual: number | null | undefined
    deltaTone: PlanFactDeltaTone
    kind?: PlanFactValueKind
    formatNumber?: (value: number) => string
}): PlanFactComparisonItem {
    const planned = params.planned ?? 0
    const actual = params.actual ?? 0
    return {
        key: params.key,
        label: params.label,
        group: params.group,
        planned,
        actual,
        delta: actual - planned,
        kind: params.kind ?? 'number',
        deltaTone: params.deltaTone,
        formatNumber: params.formatNumber,
    }
}

export function getShipmentPlanFactComparison(shipment: Shipment): PlanFactComparisonItem[] {
    const planned = shipment.calculated
    const actual = shipment.calculatedOnActual
    const plannedCopies = sumCopies(shipment.shipment_positions, 'planned')
    const actualCopies = sumCopies(shipment.shipment_positions, 'actual')
    const plannedFloods = getPlannedFloods(shipment)
    const actualFloods = shipment.print_job?.actualFloods

    return [
        numericItem({
            key: 'copies',
            label: 'Тираж',
            group: 'general',
            planned: plannedCopies,
            actual: actualCopies,
            deltaTone: 'cost',
            formatNumber: formatIntegerBySpace,
        }),
        numericItem({
            key: 'floods',
            label: 'Ручьи',
            group: 'general',
            planned: plannedFloods ?? 0,
            actual: actualFloods ?? plannedFloods ?? 0,
            deltaTone: 'cost',
        }),
        {
            key: 'materials',
            label: 'Материалы',
            group: 'general',
            planned: formatPlannedMaterials(shipment.materialRequirements),
            actual: formatActualMaterials(getShipmentMaterialConsumptions(shipment)),
            kind: 'text',
        },
        numericItem({
            key: 'materialWidth',
            label: 'Ширина роля',
            group: 'general',
            planned: planned?.materialWidth,
            actual: actual?.materialWidth,
            deltaTone: 'cost',
        }),
        numericItem({
            key: 'materialLength',
            label: 'Длина роля',
            group: 'general',
            planned: planned?.materialLength,
            actual: actual?.materialLength,
            deltaTone: 'cost',
            formatNumber: v => String(roundLength(v)),
        }),
        numericItem({
            key: 'materialWeight',
            label: 'Вес материала',
            group: 'general',
            planned: planned?.materialWeight,
            actual: actual?.materialWeight,
            deltaTone: 'cost',
            formatNumber: v => String(roundTenth(v)),
        }),
        numericItem({
            key: 'labelArea',
            label: 'Площадь этикетки',
            group: 'general',
            planned: planned?.labelArea,
            actual: actual?.labelArea,
            deltaTone: 'cost',
            formatNumber: formatIntegerBySpace,
        }),
        numericItem({
            key: 'marginRatio',
            label: 'Коеф. нац.',
            group: 'general',
            planned: planned?.marginRatio,
            actual: actual?.marginRatio,
            deltaTone: 'profit',
            formatNumber: v => String(roundHundredth(v)),
        }),
        numericItem({
            key: 'costOfOneLabel',
            label: 'Себестоимость 1 шт',
            group: 'money',
            planned: roundHundredth((planned?.costOfAllCopies || 0) / (plannedCopies || 1)),
            actual: roundHundredth((actual?.costOfAllCopies || 0) / (actualCopies || 1)),
            deltaTone: 'cost',
            formatNumber: v => String(roundHundredth(v)),
        }),
        numericItem({
            key: 'costOfAllCopies',
            label: 'Себестоимость тиража',
            group: 'money',
            planned: planned?.costOfAllCopies,
            actual: actual?.costOfAllCopies,
            deltaTone: 'cost',
            formatNumber: v => formatIntegerBySpace(Math.round(v)),
        }),
        numericItem({
            key: 'profitOfAllCopies',
            label: 'Прибыль с тиража',
            group: 'money',
            planned: planned?.profitOfAllCopies,
            actual: actual?.profitOfAllCopies,
            deltaTone: 'profit',
            formatNumber: v => formatIntegerBySpace(Math.round(v)),
        }),
        numericItem({
            key: 'turnover',
            label: 'Итого за тираж',
            group: 'money',
            planned: planned?.turnover,
            actual: actual?.turnover,
            deltaTone: 'profit',
            formatNumber: v => formatIntegerBySpace(Math.round(v)),
        }),
        numericItem({
            key: 'profitPerHour',
            label: 'Прибыль в 1 час',
            group: 'money',
            planned: planned?.profitPerHour,
            actual: actual?.profitPerHour,
            deltaTone: 'profit',
            formatNumber: v => formatIntegerBySpace(Math.round(v)),
        }),
        numericItem({
            key: 'paintCost',
            label: 'Краска',
            group: 'consumables',
            planned: planned?.paintCost,
            actual: actual?.paintCost,
            deltaTone: 'cost',
            formatNumber: v => formatIntegerBySpace(roundTenth(v)),
        }),
        numericItem({
            key: 'materialCost',
            label: 'Материал (стоимость)',
            group: 'consumables',
            planned: planned?.materialCost,
            actual: actual?.materialCost,
            deltaTone: 'cost',
            formatNumber: v => formatIntegerBySpace(roundTenth(v)),
        }),
        numericItem({
            key: 'stampCost',
            label: 'Штамп',
            group: 'consumables',
            planned: planned?.stampCost,
            actual: actual?.stampCost,
            deltaTone: 'cost',
            formatNumber: outputCalculatedValue,
        }),
        numericItem({
            key: 'formsCost',
            label: 'Формы',
            group: 'consumables',
            planned: planned?.formsCost,
            actual: actual?.formsCost,
            deltaTone: 'cost',
            formatNumber: v => formatIntegerBySpace(roundTenth(v)),
        }),
        numericItem({
            key: 'printingTime',
            label: 'Время на тираж',
            group: 'time',
            planned: planned?.printingTime,
            actual: actual?.printingTime,
            deltaTone: 'cost',
            kind: 'duration',
        }),
        numericItem({
            key: 'finishingTime',
            label: 'Время на отделку',
            group: 'time',
            planned: planned?.finishingTime,
            actual: actual?.finishingTime,
            deltaTone: 'cost',
            kind: 'duration',
        }),
        numericItem({
            key: 'commonTime',
            label: 'Общее время',
            group: 'time',
            planned: planned?.commonTime,
            actual: actual?.commonTime,
            deltaTone: 'cost',
            kind: 'duration',
        }),
    ]
}

export function formatPlanFactValue(
    item: PlanFactComparisonItem,
    value: number | string | null | undefined
): string {
    if (value == null || value === '') return '—'
    if (item.kind === 'text') return String(value)
    if (typeof value !== 'number' || !Number.isFinite(value)) return '—'
    if (item.kind === 'duration') return formatDuration(value)
    if (item.formatNumber) return item.formatNumber(value)
    return String(value)
}

export function formatPlanFactDelta(item: PlanFactComparisonItem): string | null {
    if (item.delta == null || item.kind === 'text' || item.delta === 0) return null

    const abs = Math.abs(item.delta)
    const formatted =
        item.kind === 'duration'
            ? formatDuration(abs)
            : item.formatNumber
              ? item.formatNumber(abs)
              : String(abs)

    return `${item.delta > 0 ? '+' : '−'}${formatted}`
}

export function isPlanFactDeltaGood(item: PlanFactComparisonItem): boolean {
    if (item.delta == null) return false
    return item.deltaTone === 'profit' ? item.delta > 0 : item.delta < 0
}
